<script setup>
import {ref, onMounted, computed} from 'vue'
import { useDisplay } from "vuetify"
import {handleError} from "@/utils/errors.js";
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {getContractById, getContractItems} from "@/services/api/contracts.js";
import EntityForm from "@/components/common/Form.vue";
import ButtonPreviousNext from "@/components/common/button/ButtonPreviousNext.vue";
import BasePage from "@/components/Layouts/BasePage.vue";
import {ContractEntity} from "@/entities/ContractEntity.js";
import {ContractStep} from "@/enums/enums.js"
import {searchAndSetOptions} from "@/utils/index.js";
import {getManufacturer} from "@/services/api/manufacturer.js";
import {getPOPs} from "@/services/api/POP.js";
import {getAttributeLayout} from "@/services/api/attributeLayout.js";
import {getPools} from "@/services/api/pool.js";
import {computeClass, computeClassContract} from "@/utils/styleFormats.js";
import BaseTable from "@/components/tables/BaseTable.vue";
import {formatNumber} from "@/utils/numberFormat.js";
import {formatDateByLocale} from "@/utils/dateTimeFormat.js";
import Text from "@/components/common/Text.vue";
import SkeletonLoader from "@/components/common/SkeletonLoader.vue";
import GroupList from "@/components/common/GroupList.vue";

const { t, locale } = useI18n();
const loading = ref(false);
const loadingMounted = ref(false);
const route = useRoute();
const disabledInput = ref(false);
const havePermission = ref(false);
const form = ref(null)
const step = ref(ContractStep.Contract);
const totalItems = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const Contract = new ContractEntity();
const entity = ref(Contract);
const formDataStep1 = ref(entity.value.fieldsStep1);
const formDataStep2 = ref(entity.value.fieldsStep2);
const id = route.params.id;
const isFetching = ref(false);
const { smAndDown } = useDisplay();
const items = ref([]);
const router = useRouter();

async function fetchData() {
    try {
        loading.value = true;
        const response = await getContractItems({ id });
        items.value = Array.isArray(response) ? response : response ? [response] : [];

        totalItems.value = {
            "totalItems": items.value.length,
            "totalPages": 1,
            "limit": items.value.length,
            "page": 1
        }

        items.value = items.value.map(item => ({
            ...item,
            product: item.product
        }));

    } catch (error) {
        console.error(error);
        message.error(await handleError(error, t));
    } finally {
        loading.value = false;
    }
}

onMounted(async () => {
    try {
        if (!id) return;

        loadingMounted.value = false;
        const { data } = await getContractById({ id });
        const mapFields = [
            "customer"
        ];

        mapFields.forEach(field => {
            const entity = data[field];
            if (entity && entity.address && typeof entity.address === "object") {
                const addr = entity.address;

                data[`${field}_address_line1`] = addr.line1 || "";
                data[`${field}_address_line2`] = addr.line2 || "";
                data[`${field}_address_district`] = addr.district || "";
                data[`${field}_address_postal_code`] = addr.postal_code || "";

                if (addr.city && typeof addr.city === "object") {
                    data[`${field}_address_city_name`] = addr.city.name || "";
                    data[`${field}_address_city_state`] = addr.city.state || "";
                }
            }

            if (entity && typeof entity === "object") {
                data[`${field}_name`] = entity.name;
            }
        });

        formDataStep1.value = data;
        formDataStep2.value = data;
        await fetchData();
    } catch (e) {
        console.error(e);
    } finally {
        loadingMounted.value = true;
    }
});

function goToItem(url) {
    router.push(url);
}

</script>

<template>
    <BasePage :loading-mounted="!loading" :entity="entity">
        <template #default>
            <v-card class="bg-white trezo-card  px-sm-0 pb-10">
                <v-tabs v-model="step" class="mb-5">
                    <v-tab class="text-dark router-link-active button-route me-4" :value="ContractStep.Contract">
                        {{ t('components.contracts.listContracts.contract') }}
                    </v-tab>
                    <v-tab class="text-dark button-route" :value="ContractStep.Services">
                        {{ t('components.contracts.listContracts.services') }}
                    </v-tab>
                </v-tabs>
                <v-window v-model="step">
                    <v-window-item :value="ContractStep.Contract">
                        <EntityForm :entity="entity" fieldsToUse="fieldsStep1" ref="form" :disabled="true" v-model="formDataStep1"  />

                        <div class="d-flex justify-end mt-4">
                            <ButtonPreviousNext  :next="true" @click="step = ContractStep.Services" />
                        </div>
                    </v-window-item>
                    <v-window-item :value="ContractStep.Services">

                        <v-card class="box bg-body-bg mb-5">
                            <div class="d-flex align-items-center p-3">
                                <div v-if="items">
                                    <Text tag="h5" >{{ t('components.contracts.listContracts.contractValue') }}</Text>
                                    <Text tag="h5" >{{ formatNumber(items[0].contract.total_value, locale.value)}}</Text>
                                </div>
                            </div>
                        </v-card>
                        <Text class="pt-3" tag="h5">{{ t("common.labels.plans")}}</Text>
                        <GroupList
                            :items="items"
                            groupKey="groupname"
                            titleKey="name"
                            groupIcon="visibility"
                            connectionIcon="visibility"
                            @view="goToItem"
                        />

                        <div class="d-flex justify-start mt-10">
                            <ButtonPreviousNext size="sm" :next="false" @click="step = ContractStep.Contract" />
                        </div>

                    </v-window-item>
                </v-window>
            </v-card>
        </template>
    </BasePage>

</template>
